import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Linkedin, Building2, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../config/siteConfig';
import PageHeader from '../components/PageHeader';

const BEN_IMAGE = '/other/Ben.webp';

const Contact: React.FC = () => {
    const { t } = useLanguage();

    return (
        <div className="bg-maat-stone min-h-screen">
            <PageHeader
                tag={t.contact.tag}
                title={t.contact.title}
                titleAccent={t.contact.titleAccent}
                subtitle={t.contact.subtitle}
            />

            <section className="py-24">
                <div className="container mx-auto px-6 max-w-5xl">
                    {/* The Premium Business Card */}
                    <div className="bg-white rounded-3xl md:rounded-[3rem] border border-maat-sand shadow-2xl overflow-hidden flex flex-col md:flex-row">
                        
                        {/* Left Side: Personal Anchor (Dark Panel) */}
                        <div className="bg-maat-blue text-maat-stone p-10 md:p-16 flex flex-col items-center justify-center md:w-2/5 text-center relative overflow-hidden">
                            <div className="relative z-10">
                                <img 
                                    src={BEN_IMAGE} 
                                    alt="Ben Degens" 
                                    className="w-32 h-32 md:w-44 md:h-44 rounded-full object-cover border-4 border-maat-gold/30 shadow-2xl mx-auto mb-6 hover:scale-105 transition-transform duration-500"
                                />
                                <h3 className="text-2xl font-serif text-white mb-6">Ben Degens</h3>
                                <p className="text-maat-stone/80 font-light leading-relaxed mb-8">
                                    {t.contact.description}
                                </p>
                                <Link
                                    to="/about"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-maat-gold/50 rounded-full text-maat-gold font-medium hover:bg-maat-gold hover:text-maat-blue transition-all duration-300"
                                >
                                    {t.contact.whoIsBen} <ArrowRight size={18} />
                                </Link>
                            </div>
                        </div>

                        {/* Right Side: Quick Actions & Details */}
                        <div className="p-10 md:p-16 flex flex-col h-full md:w-3/5 bg-white">
                            
                            {/* Action Cards */}
                            <h3 className="text-3xl md:text-4xl font-serif text-maat-blue mb-10">{t.contact.contactInformation}</h3>
                            <div className="space-y-4 sm:space-y-6 mb-12">
                                <a href={`mailto:${t.contact.emailValue}`} className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 rounded-2xl border border-maat-sand bg-maat-stone/30 hover:bg-white hover:border-maat-gold transition-all duration-300 shadow-sm hover:shadow-md group">
                                    <div className="p-3 sm:p-4 bg-white rounded-xl group-hover:bg-maat-gold/10 transition-colors shrink-0">
                                        <Mail className="text-maat-gold w-6 h-6 sm:w-7 sm:h-7" />
                                    </div>
                                    <div className="text-left overflow-hidden">
                                        <p className="text-maat-blue/50 text-xs font-bold uppercase tracking-widest mb-1">{t.contact.emailLabel}</p>
                                        <p className="text-base sm:text-lg md:text-xl text-maat-blue font-serif group-hover:text-maat-gold transition-colors truncate">{t.contact.emailValue}</p>
                                    </div>
                                </a>

                                {siteConfig.social.mobile && (
                                    <div className="p-4 sm:p-6 rounded-2xl border border-maat-sand bg-maat-stone/30 shadow-sm">
                                        <div className="flex items-center gap-4 sm:gap-6 mb-4">
                                            <div className="p-3 sm:p-4 bg-white rounded-xl shrink-0">
                                                <Phone className="text-maat-gold w-6 h-6 sm:w-7 sm:h-7" aria-hidden="true" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-maat-blue/50 text-xs font-bold uppercase tracking-widest mb-1">{t.contact.mobileLabel}</p>
                                                <p className="text-base sm:text-lg md:text-xl text-maat-blue font-serif">{siteConfig.social.mobile}</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-3">
                                            <a
                                                href={`tel:${siteConfig.social.mobile.replace(/[^+\d]/g, '')}`}
                                                aria-label={`${t.contact.call} ${siteConfig.social.mobile}`}
                                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-maat-blue text-maat-stone text-sm font-bold hover:bg-maat-gold hover:text-maat-blue transition-colors"
                                            >
                                                <Phone size={16} aria-hidden="true" /> {t.contact.call}
                                            </a>
                                            <a
                                                href={`https://wa.me/${siteConfig.social.mobile.replace(/\D/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`${t.contact.whatsapp} ${siteConfig.social.mobile}`}
                                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-maat-sand bg-white text-maat-blue text-sm font-bold hover:border-maat-gold hover:bg-maat-gold/10 transition-colors"
                                            >
                                                <MessageCircle size={16} aria-hidden="true" /> {t.contact.whatsapp}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                <a href="https://www.linkedin.com/in/bendegens/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 rounded-2xl border border-maat-sand bg-maat-stone/30 hover:bg-white hover:border-maat-gold transition-all duration-300 shadow-sm hover:shadow-md group">
                                    <div className="p-3 sm:p-4 bg-white rounded-xl group-hover:bg-maat-gold/10 transition-colors shrink-0">
                                        <Linkedin className="text-maat-gold w-6 h-6 sm:w-7 sm:h-7" />
                                    </div>
                                    <div className="text-left overflow-hidden">
                                        <p className="text-maat-blue/50 text-xs font-bold uppercase tracking-widest mb-1">{t.contact.linkedinLabel}</p>
                                        <p className="text-base sm:text-lg md:text-xl text-maat-blue font-serif group-hover:text-maat-gold transition-colors truncate">linkedin.com/in/bendegens</p>
                                    </div>
                                </a>
                            </div>

                            {/* Minimal Footer Inside the Card */}
                            <div className="mt-auto pt-8 border-t border-maat-sand/50">
                                <div className="flex flex-col gap-3 text-maat-blue/70 text-sm font-light">
                                    <p className="flex items-center gap-2">
                                        <Building2 size={16} className="text-maat-gold shrink-0" />
                                        <span className="leading-tight">
                                            <span className="font-medium text-maat-blue">{siteConfig.legalName}</span>
                                        </span>
                                    </p>
                                    <p className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 pl-[24px]">
                                        <span><span className="font-medium">{t.company.kvkLabel}</span> {siteConfig.kvk}</span>
                                        {siteConfig.vat && <span className="hidden sm:inline text-maat-sand/50">&bull;</span>}
                                        {siteConfig.vat && <span><span className="font-medium">{t.company.vatLabel}</span> {siteConfig.vat}</span>}
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
